import { useEffect } from "react";
import { checkIsValidPreviewOrigin } from "repoV2/utils/previewPages";
import { isDefined } from "repoV2/utils/common/dataTypes/common";
import {
    getPocessedBonusesListFromApi,
    getPocessedFeaturedInListFromApi,
    getPocessedTestimonialsListFromApi,
    getProcessedContentOutlineListFromApi,
    getProcessedCustomHighlightsListFromApi,
    getProcessedCustomPromoStatsListFromApi,
    getProcessedGalleryImgListFromApi,
    getProcessedCrossDataShowcaseListFromApi,
    getProcessedDemoVideoHighlightsListFromApi,
} from "./apis/getSalePageTemplateDataApi/getSalePageTemplateDataApi.utils";
import { ISalePageContextValue } from "../SalePageContext";
import { IRecordedContentCatalogueDataFromApi } from "./apis/getRecordedContentCatalogueDataApi/getRecordedContentCatalogueDataApi.interfaces";
import { ADD_VIDEO_OPTIONS } from "../constants";

export const useSalePageBuilder = ({
    contextValue,
    setContextValue,
    recordedContentCatalogueItemsList,
}: {
    contextValue: ISalePageContextValue;
    setContextValue: React.Dispatch<
        React.SetStateAction<ISalePageContextValue>
    >;
    recordedContentCatalogueItemsList?: IRecordedContentCatalogueDataFromApi;
}) => {
    useEffect(() => {
        const listenerFn = (e: MessageEvent<any>) => {
            if (!checkIsValidPreviewOrigin(e.origin)) return;

            const salePageBuilderPayload = e?.data || {};
            const salePageBuilderSection = salePageBuilderPayload.section || {};
            const newSectionsData = contextValue.sectionsData;
            const newColourPalette = contextValue.colorPalette;
            const salePageBuilderSectionData =
                salePageBuilderSection.section_data || {};

            // tags: #test
            switch (salePageBuilderSection.slug) {
                case "AboutSection":
                    newSectionsData.aboutSection = {
                        title: salePageBuilderSectionData.title,
                        shortDescription:
                            salePageBuilderSectionData.short_description,
                        overline: salePageBuilderSectionData.overline,
                        sliderImgList: salePageBuilderSectionData.sliderImgList,
                        progress: salePageBuilderSectionData.progress,
                    };
                    break;
                case "GuranteeSection":
                    newSectionsData.guranteeSection = {
                        title: salePageBuilderSectionData.title,
                        shortDescription:
                            salePageBuilderSectionData.short_description,
                        imgSrc: salePageBuilderSectionData.imgSrc,
                        description: salePageBuilderSectionData.description,
                        sliderImgList: salePageBuilderSectionData.sliderImgList,
                    };
                    break;
                case "MoneybackSection":
                    newSectionsData.MoneybackSection = {
                        imgSrc: salePageBuilderSectionData.cover_image,
                        title: salePageBuilderSectionData.title,
                    };
                    break;

                case "PainPointsSection":
                    newSectionsData.PainPointsSection = {
                        title: salePageBuilderSectionData.section_name,
                        CheckboxList: salePageBuilderSectionData.data,
                    };
                    break;

                case "StickyFooterSection":
                    newSectionsData.stickyFooterSection = {
                        title: salePageBuilderSectionData.title,
                        description: salePageBuilderSectionData.description,
                        showTimer: salePageBuilderSectionData.showTimer,
                        timerText: salePageBuilderSectionData.timertext,
                    };
                    break;

                case "ContactSection":
                    newSectionsData.ContactSection = {
                        imgSrc: salePageBuilderSectionData.contact_img,
                        title: salePageBuilderSectionData.title,
                        description: salePageBuilderSectionData.description,
                    };
                    break;
                case "BannerImage": {
                    const { videoUploadType } = salePageBuilderSectionData;
                    let videoSrc;
                    switch (videoUploadType) {
                        case ADD_VIDEO_OPTIONS.UPLOAD_VIDEO.slug:
                            videoSrc =
                                salePageBuilderSectionData.video_upload_url;
                            break;
                        case ADD_VIDEO_OPTIONS.ADD_LINK.slug:
                            videoSrc = salePageBuilderSectionData.video_url;
                            break;
                        case ADD_VIDEO_OPTIONS.ADD_EMBED_CODE.slug:
                            videoSrc =
                                salePageBuilderSectionData.video_embed_code;
                            break;
                        default:
                            break;
                    }
                    newSectionsData.bannerImage = {
                        mediaType: salePageBuilderSectionData.view_type,
                        videoSrc,
                        videoSrcType: videoUploadType,
                        imgSrc: salePageBuilderSectionData.cover_image,
                        title: salePageBuilderSectionData.title,
                        disableAutoPlay:
                            salePageBuilderSectionData.disableAutoPlay,
                        hideControls: salePageBuilderSectionData.hideControls,
                    };
                    break;
                }
                case "RegisterButton":
                    newSectionsData.mainCtaButton = {
                        ctaText: salePageBuilderSectionData.ctaText,
                        strikedOffText:
                            salePageBuilderSectionData.strikedOffText,
                        delayTime: salePageBuilderSectionData.delayTime,
                    };
                    break;
                case "Requirements": {
                    const requirementsSectionData =
                        salePageBuilderSectionData?.data?.[0]?.data || [];
                    newSectionsData.requirementsSection = {
                        title: salePageBuilderSectionData.section_name,
                        requirementsList: requirementsSectionData,
                        description: salePageBuilderSectionData.description,
                    };
                    break;
                }
                case "FAQs":
                    newSectionsData.faqsSection = {
                        title: salePageBuilderSectionData.section_name,
                        faqsList: salePageBuilderSectionData.data,
                    };
                    break;
                case "Contact":
                    newSectionsData.contactCreatorSection = {
                        title: salePageBuilderSectionData.title,
                        description: salePageBuilderSectionData.description,
                        getInTouchCtaText:
                            salePageBuilderSectionData.getInTouchCtaText,
                        getInTouchCustomCtaUrl:
                            salePageBuilderSectionData.getInTouchCustomCtaUrl,
                    };
                    break;
                case "CountdownTimer":
                    newSectionsData.countdownTimer = {
                        timerRunninngOutText:
                            salePageBuilderSectionData.timer_running_out_text,
                        offerName: salePageBuilderSectionData.hurry_text,
                        startTime: salePageBuilderSectionData.start_datetime,
                        endTime: salePageBuilderSectionData.end_datetime,
                        restartCounter:
                            salePageBuilderSectionData.restart_counter,
                    };
                    break;

                case "BonusSection":
                    newSectionsData.bonusesSection = {
                        title: salePageBuilderSectionData.section_name,
                        bonusesList: getPocessedBonusesListFromApi(
                            salePageBuilderSectionData.data
                        ),
                    };
                    break;
                case "Testimonials":
                    newSectionsData.testimonialsSection = {
                        title: salePageBuilderSectionData.section_name,
                        testimonialsList: getPocessedTestimonialsListFromApi(
                            salePageBuilderSectionData.data
                        ),
                        listDesign: salePageBuilderSectionData.list_design,
                    };
                    break;
                case "Highlights": {
                    newSectionsData.highlightsSection = {
                        title: salePageBuilderSectionData.section_name,
                        highlightsList:
                            salePageBuilderSectionData?.data?.[0]?.data,
                    };
                    break;
                }
                case "Certificates": {
                    const { videoUploadType } = salePageBuilderSectionData;
                    let videoSrc;
                    switch (videoUploadType) {
                        case ADD_VIDEO_OPTIONS.UPLOAD_VIDEO.slug:
                            videoSrc =
                                salePageBuilderSectionData.video_upload_url;
                            break;
                        case ADD_VIDEO_OPTIONS.ADD_LINK.slug:
                            videoSrc = salePageBuilderSectionData.video_url;
                            break;
                        case ADD_VIDEO_OPTIONS.ADD_EMBED_CODE.slug:
                            videoSrc =
                                salePageBuilderSectionData.video_embed_code;
                            break;
                        default:
                            break;
                    }
                    newSectionsData.certificatesSection = {
                        title: salePageBuilderSectionData.section_name,
                        description:
                            salePageBuilderSectionData.short_description,
                        imgSrc: salePageBuilderSectionData.cover_image,
                        videoSrc,
                        videoSrcType: videoUploadType,
                        mediaType: salePageBuilderSectionData.view_type,
                        pointersList: salePageBuilderSectionData.pointers_list,
                        hideControls: salePageBuilderSectionData?.hideControls,
                        disableAutoPlay: isDefined(
                            salePageBuilderSectionData?.disableAutoPlay
                        )
                            ? salePageBuilderSectionData?.disableAutoPlay
                            : true,
                    };
                    break;
                }
                case "BackgroundImage":
                    newSectionsData.backgroundImage = {
                        mediaType: salePageBuilderSectionData.view_type,
                        videoSrc:
                            salePageBuilderSectionData.background_video_url,
                        imgSrc: salePageBuilderSectionData.background_image,
                    };
                    break;
                case "FeaturedIn":
                    newSectionsData.featuredInSection = {
                        title: salePageBuilderSectionData.section_name,
                        featuredInList: getPocessedFeaturedInListFromApi(
                            salePageBuilderSectionData.data
                        ),
                    };
                    break;
                case "HowToBook":
                    newSectionsData.howToParticipateSection = {
                        title: salePageBuilderSectionData.section_name,
                        howToParticipateList: salePageBuilderSectionData.data,
                    };
                    break;
                case "PromotionalStats":
                    newSectionsData.promotionalStatsSection = {
                        userCount: {
                            value: salePageBuilderSectionData.count_data?.[0]
                                ?.value,
                            description:
                                salePageBuilderSectionData.count_data?.[0]
                                    ?.description,
                        },
                        rating: {
                            value: salePageBuilderSectionData.rating_data?.[0]
                                ?.value,
                            description:
                                salePageBuilderSectionData.rating_data?.[0]
                                    ?.description,
                        },
                    };
                    break;
                case "WhatYouWillGetToDo":
                    newSectionsData.whatYouWillDoSection = {
                        title: salePageBuilderSectionData.section_name,
                        description: salePageBuilderSectionData.description,
                    };
                    break;
                case "WhoIsItFor":
                    newSectionsData.whoIsItForSection = {
                        title: salePageBuilderSectionData.section_name,
                        whoIsItForList: salePageBuilderSectionData.data,
                    };
                    break;
                case "WhatYouWillLearn":
                    newSectionsData.whatYouWillLearn = {
                        title: salePageBuilderSectionData.section_name,
                        learnings: salePageBuilderSectionData.data,
                    };
                    break;
                case "ContentCatalogue":
                    newSectionsData.contentCatalogueSection = {
                        title: salePageBuilderSectionData.section_name,
                        pointersList: salePageBuilderSectionData.pointersList,
                        catalogueItemsList:
                            salePageBuilderSectionData.catalogueItemsList ||
                            recordedContentCatalogueItemsList,
                    };
                    break;
                case "DesiredOutcomes":
                    newSectionsData.DesiredOutcomesSection = {
                        title: salePageBuilderSectionData.section_name,
                        desiredOutcomesList:
                            salePageBuilderSectionData.desiredOutcomesList,
                        caption: salePageBuilderSectionData.caption,
                    };
                    break;

                case "DemonstrationVideo": {
                    const { videoUploadType } = salePageBuilderSectionData;
                    let demoVideoSrc;
                    switch (videoUploadType) {
                        case ADD_VIDEO_OPTIONS.UPLOAD_VIDEO.slug:
                            demoVideoSrc =
                                salePageBuilderSectionData.demo_video_upload_url;
                            break;
                        case ADD_VIDEO_OPTIONS.ADD_LINK.slug:
                            demoVideoSrc =
                                salePageBuilderSectionData.demo_video_url;
                            break;
                        case ADD_VIDEO_OPTIONS.ADD_EMBED_CODE.slug:
                            demoVideoSrc =
                                salePageBuilderSectionData.demo_video_embed_code;
                            break;
                        default:
                            break;
                    }
                    newSectionsData.demoVideoSection = {
                        title: salePageBuilderSectionData.title,
                        demoVideoSrc,
                        demoVideoSrcType: videoUploadType,
                        disableAutoPlay:
                            salePageBuilderSectionData.disableAutoPlay,
                        hideControls: salePageBuilderSectionData.hideControls,
                    };
                    break;
                }
                case "CustomPromotionalStats":
                    newSectionsData.customPromoStatsSection = {
                        title: salePageBuilderSectionData.section_name,
                        customPromoStatsList:
                            getProcessedCustomPromoStatsListFromApi(
                                salePageBuilderSectionData.data
                            ),
                    };
                    break;

                case "AboutCreator":
                    newSectionsData.aboutCreatorSection = {
                        title: salePageBuilderSectionData.title,
                        shortDescription:
                            salePageBuilderSectionData.short_description,
                        description: salePageBuilderSectionData.description,
                        creatorImg: salePageBuilderSectionData.creator_img,
                        socialStatsList:
                            salePageBuilderSectionData.socialStatsList,
                        sliderImgList: salePageBuilderSectionData.sliderImgList,
                    };
                    break;

                case "CircularBannerImage":
                    newSectionsData.circularBannerImageSection = {
                        circularBannerImg:
                            salePageBuilderSectionData.circular_banner_img,
                    };
                    break;

                case "CrossDataShowcase":
                    newSectionsData.crossDataShowcase = {
                        title: salePageBuilderSectionData.section_name,
                        description: salePageBuilderSectionData.description,
                        showcaseList: getProcessedCrossDataShowcaseListFromApi(
                            salePageBuilderSectionData.data
                        ),
                    };
                    break;

                case "DemoVideoHighlights":
                    newSectionsData.demoVideoHighlights = {
                        highlightsList:
                            getProcessedDemoVideoHighlightsListFromApi(
                                salePageBuilderSectionData.data
                            ),
                    };
                    break;

                case "ColourPalette": {
                    const colorPaletteList =
                        salePageBuilderSectionData?.color_palette?.data || [];
                    colorPaletteList.forEach((j: any) => {
                        switch (j.type) {
                            case "gradient":
                                j.value.forEach((k: string, kIdx: number) => {
                                    newColourPalette[`color${j.id}_${kIdx}`] =
                                        k;
                                });
                                break;
                            case "single":
                                [newColourPalette[`color${j.id}`]] = j.value;
                                break;
                            default:
                                break;
                        }
                    });
                    break;
                }
                case "ThisCouldBeYourLife":
                    newSectionsData.thisCouldBeYourLife = {
                        title: salePageBuilderSectionData.section_name,
                        lifeItemsList: salePageBuilderSectionData.data,
                    };
                    break;

                case "Awards":
                    newSectionsData.awards = {
                        title: salePageBuilderSectionData.section_name,
                        awardItemsList: salePageBuilderSectionData.data,
                    };
                    break;

                case "WhatWillChangeInYourLife":
                    newSectionsData.whatWillChangeInYourLife = {
                        title: salePageBuilderSectionData.section_name,
                        itemsList: salePageBuilderSectionData.data,
                    };
                    break;

                case "CoachIntro": {
                    const coachIntroSectionData =
                        salePageBuilderSectionData.coach_intro_data?.[0];
                    const ratingData =
                        salePageBuilderSectionData.rating_data?.[0];
                    newSectionsData.coachIntroSection = {
                        coachIntro: {
                            title: coachIntroSectionData?.title,
                            description: coachIntroSectionData?.description,
                            imgSrc: coachIntroSectionData?.imgSrc,
                        },
                        rating: {
                            value: ratingData?.rating,
                            description: ratingData?.rating_desc,
                        },
                    };
                    break;
                }
                case "MultipleBackgroundImageSection":
                    newSectionsData.multipleBackgroundImageSection = {
                        bgImages: salePageBuilderSectionData?.bg_images || [],
                    };
                    break;

                case "HeroSection":
                    newSectionsData.heroSection = {
                        title: salePageBuilderSectionData?.title,
                        description:
                            salePageBuilderSectionData?.short_description,
                        logo: salePageBuilderSectionData?.logo,
                    };
                    break;

                case "ActionHighlightButton":
                    newSectionsData.actionHighlight = {
                        ctaText: salePageBuilderSectionData?.ctaText,
                        strikedOffText:
                            salePageBuilderSectionData?.strikedOffText,
                        underlineText:
                            salePageBuilderSectionData?.underlineText,
                        delayTime: salePageBuilderSectionData?.delayTime,
                    };
                    break;

                case "CustomHighlights":
                    newSectionsData.customHighlights = {
                        title: salePageBuilderSectionData?.section_name,
                        customHighlightsList:
                            getProcessedCustomHighlightsListFromApi(
                                salePageBuilderSectionData?.data
                            ),
                    };
                    break;

                case "CallToAction":
                    newSectionsData.callToAction = {
                        title: salePageBuilderSectionData?.title,
                        description:
                            salePageBuilderSectionData?.short_description,
                    };
                    break;

                case "AboutBrand":
                    newSectionsData.aboutBrandSection = {
                        title: salePageBuilderSectionData?.title,
                        description:
                            salePageBuilderSectionData?.short_description,
                        brandLogo: salePageBuilderSectionData?.brand_logo,
                    };
                    break;

                case "GallerySection": {
                    const { videoUploadType } = salePageBuilderSectionData;
                    let videoSrc;
                    switch (videoUploadType) {
                        case ADD_VIDEO_OPTIONS.UPLOAD_VIDEO.slug:
                            videoSrc =
                                salePageBuilderSectionData.gallery_video_upload_url;
                            break;
                        case ADD_VIDEO_OPTIONS.ADD_LINK.slug:
                            videoSrc =
                                salePageBuilderSectionData.gallery_video_url;
                            break;
                        case ADD_VIDEO_OPTIONS.ADD_EMBED_CODE.slug:
                            videoSrc =
                                salePageBuilderSectionData.gallery_video_embed_code;
                            break;
                        default:
                            break;
                    }
                    newSectionsData.gallerySection = {
                        galleryVideoSrc: videoSrc,
                        galleryVideoSrcType: videoUploadType,
                        galleryBannerImg:
                            salePageBuilderSectionData.gallery_img_url,
                        galleryImgList: getProcessedGalleryImgListFromApi(
                            salePageBuilderSectionData.data
                        ),
                        hideControls: salePageBuilderSectionData.hideControls,
                        disableAutoPlay: isDefined(
                            salePageBuilderSectionData.disableAutoPlay
                        )
                            ? salePageBuilderSectionData.disableAutoPlay
                            : true,
                    };
                    break;
                }
                case "ContentOutline":
                    newSectionsData.contentOutline = {
                        title: salePageBuilderSectionData?.section_name,
                        description: salePageBuilderSectionData?.section_desc,
                        contentOutlineList:
                            getProcessedContentOutlineListFromApi(
                                salePageBuilderSectionData?.data
                            ),
                    };
                    break;

                case "StickyText":
                    newSectionsData.stickyText = {
                        title: salePageBuilderSectionData.title,
                    };
                    break;

                default:
                    break;
            }
            setContextValue({
                ...contextValue,
                sectionsData: JSON.parse(JSON.stringify(newSectionsData)),
                colorPalette: JSON.parse(JSON.stringify(newColourPalette)),
            });
        };

        window.addEventListener("message", listenerFn);
        return () => {
            window.removeEventListener("message", listenerFn);
        };
    }, [contextValue]);
};
